import { useCallback, useEffect, useRef } from 'react';
import {
  FAMILY_SYNC_DEBOUNCE_MS,
  FAMILY_SYNC_INTERVAL_MS,
  loadFamilySyncPrefs,
  saveFamilySyncPrefs,
} from '../lib/familySync';
import { loadBackupFromCloud, updateBackupInCloud } from '../lib/backupApi';
import { emptyAccount, type BackupPayload } from '../lib/userStore';
import { mergeCustomizations } from '../lib/customizations';

type Options = {
  enabled: boolean;
  checkedSignature: string;
  exportBackup: () => BackupPayload | null;
  applyFamilyShare: (payload: BackupPayload) => { ok: boolean };
};

function mergePayloads(local: BackupPayload, remote: BackupPayload): BackupPayload {
  const a = emptyAccount(local.account);
  const b = emptyAccount(remote.account);
  return {
    version: 2,
    savedAt: new Date().toISOString(),
    account: emptyAccount({
      ...a,
      dueDate: b.dueDate ?? a.dueDate,
      birthDate: b.birthDate ?? a.birthDate,
      checked: Array.from(new Set([...(a.checked || []), ...(b.checked || [])])),
      customizations: mergeCustomizations(a.customizations, b.customizations),
      onboarded: true,
    }),
  };
}

/**
 * 一度共有コードでつながった端末どうしを、起動・フォーカス・定期・変更時に自動同期する。
 * ローカルのチェック履歴（localStorage）は消さない。
 */
export function useFamilyAutoSync({
  enabled,
  checkedSignature,
  exportBackup,
  applyFamilyShare,
}: Options) {
  const syncingRef = useRef(false);
  const debounceRef = useRef<number | null>(null);
  const lastSignatureRef = useRef(checkedSignature);

  const syncNow = useCallback(async () => {
    if (!enabled) return { ok: false as const };
    const prefs = loadFamilySyncPrefs();
    if (!prefs?.autoSync || !prefs.code) return { ok: false as const };
    if (syncingRef.current) return { ok: false as const };
    syncingRef.current = true;
    try {
      const local = exportBackup();
      if (!local) return { ok: false as const };

      const remote = await loadBackupFromCloud(prefs.code);
      let toPush = local;
      if (remote.ok) {
        toPush = mergePayloads(local, remote.payload);
        applyFamilyShare(remote.payload);
      }

      const pushed = await updateBackupInCloud(prefs.code, toPush);
      if (!pushed.ok) return { ok: false as const };
      saveFamilySyncPrefs({
        ...prefs,
        lastSyncAt: new Date().toISOString(),
      });
      return { ok: true as const };
    } catch {
      return { ok: false as const };
    } finally {
      syncingRef.current = false;
    }
  }, [enabled, exportBackup, applyFamilyShare]);

  useEffect(() => {
    if (!enabled) return;
    void syncNow();
    const id = window.setInterval(() => {
      void syncNow();
    }, FAMILY_SYNC_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, syncNow]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => {
      void syncNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void syncNow();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, syncNow]);

  useEffect(() => {
    if (!enabled) return;
    if (checkedSignature === lastSignatureRef.current) return;
    lastSignatureRef.current = checkedSignature;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void syncNow();
    }, FAMILY_SYNC_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [enabled, checkedSignature, syncNow]);

  return { syncNow };
}
